import {
  ClientBusinessProfileVariant,
  CombinedUserData,
  CreateUserType,
  PrivateClientFinancingProfile,
} from '@otw/models'
import { encryptPayload } from 'core/security/encryptPayload'
import { axiosClient, axiosPublicClient } from '../axiosClient'
import { fileToBase64 } from '@/core/utils/fileUtils'

export interface GetAllUsersResponse {
  users: CombinedUserData[]
  /** Present on the first page only; subsequent pages omit this to avoid redundant count queries. */
  total?: number
  page?: number
  limit?: number
  hasMore?: boolean
  lastDocId?: string | null
}

export interface ClientFinancingProfileResponse {
  id: string
  email: string
  first_name: string
  last_name: string
  company_name: string
  dealership_name: string
  financingProfile: PrivateClientFinancingProfile | null
  error?: string
}

export const portalApi = {
  client: {
    /** @encrypted This method encrypts the financing profile before transmission */
    createClientPrivateFinancingProfile: async (
      financingProfile: Omit<PrivateClientFinancingProfile, 'id'>,
    ): Promise<any> => {
      const encryptedPayload = await encryptPayload(financingProfile)
      return await axiosClient.post('/portal/client/create-client-private-financing-profile', { encryptedPayload })
    },

    /** @encrypted This method receives and decrypts the financing profile */
    getClientPrivateFinancingProfile: async (): Promise<any> => {
      return await axiosClient.get('/portal/client/get-client-private-financing-profile')
    },

    uploadDriverLicenseFront: async (
      photoBase64: string,
      contentType?: string,
    ): Promise<{ storagePath: string; url?: string }> => {
      return await axiosClient.post('/portal/client/driver-license-front/upload', {
        photo: photoBase64,
        contentType,
      })
    },

    getDriverLicenseFrontUrl: async (storagePath: string): Promise<{ url: string }> => {
      const params = new URLSearchParams({ storagePath })
      return await axiosClient.get(`/portal/client/driver-license-front/url?${params.toString()}`)
    },

    removeDriverLicenseFront: async (): Promise<void> => {
      await axiosClient.post('/portal/client/driver-license-front/remove')
    },
  },

  admin: {
    getAuthUsers: async (options?: {
      limit?: number
      page?: number
      lastDocId?: string | null
      search?: string
    }): Promise<GetAllUsersResponse> => {
      const params = new URLSearchParams()
      if (options?.limit) params.append('limit', options.limit.toString())
      if (options?.page) params.append('page', options.page.toString())
      if (options?.lastDocId && options.lastDocId !== null) params.append('lastDocId', options.lastDocId)
      if (options?.search) params.append('search', options.search)

      const queryString = params.toString() ? `?${params.toString()}` : ''
      return await axiosClient.get(`/portal/admin/get-auth-users${queryString}`)
    },

    createFirebaseUser: async (userToCreate: CreateUserType): Promise<any> => {
      return await axiosClient.post('/portal/admin/create-firebase-user', userToCreate)
    },

    createDisabledFirebaseUser: async (userToCreate: CreateUserType): Promise<any> => {
      return await axiosClient.post('/portal/admin/create-disabled-firebase-user', userToCreate)
    },

    createLogisticsUser: async (userToCreate: CreateUserType): Promise<any> => {
      return await axiosClient.post('/portal/admin/create-logistics-user', userToCreate)
    },

    uploadTransporterLicenseFront: async (
      photoBase64: string,
      companyId?: string,
      transporterId?: string
    ): Promise<{ storagePath: string; url?: string }> => {
      return await axiosClient.post('/portal/admin/transporter-license-front/upload', {
        photo: photoBase64,
        companyId,
        transporterId,
      })
    },

    getTransporterLicenseFrontUrl: async (
      storagePath: string
    ): Promise<{ url: string }> => {
      const params = new URLSearchParams({ storagePath })
      return await axiosClient.get(`/portal/admin/transporter-license-front/url?${params.toString()}`)
    },

    uploadClientDriverLicenseFront: async (
      photoBase64: string,
      clientUserId: string,
      contentType?: string,
    ): Promise<{ storagePath: string; url?: string }> => {
      return await axiosClient.post('/portal/admin/client-driver-license-front/upload', {
        photo: photoBase64,
        clientUserId,
        contentType,
      })
    },

    getClientDriverLicenseFrontUrl: async (
      storagePath: string,
      clientUserId: string
    ): Promise<{ url: string }> => {
      const params = new URLSearchParams({ storagePath, clientUserId })
      return await axiosClient.get(`/portal/admin/client-driver-license-front/url?${params.toString()}`)
    },

    removeClientDriverLicenseFront: async (clientUserId: string): Promise<void> => {
      await axiosClient.post('/portal/admin/client-driver-license-front/remove', { clientUserId })
    },

    uploadContactDriverLicenseFront: async (
      photoBase64: string,
      contactId: string,
      contentType?: string,
    ): Promise<{ storagePath: string; url?: string }> => {
      return await axiosClient.post('/portal/admin/contact-driver-license-front/upload', {
        photo: photoBase64,
        contactId,
        contentType,
      })
    },

    getContactDriverLicenseFrontUrl: async (
      storagePath: string,
      contactId: string
    ): Promise<{ url: string }> => {
      const params = new URLSearchParams({ storagePath, contactId })
      return await axiosClient.get(`/portal/admin/contact-driver-license-front/url?${params.toString()}`)
    },

    removeContactDriverLicenseFront: async (contactId: string): Promise<void> => {
      await axiosClient.post('/portal/admin/contact-driver-license-front/remove', { contactId })
    },

    transferContactDriverLicenseToUser: async (
      contactId: string,
      clientUserId: string
    ): Promise<{ transferred: boolean; storagePath?: string }> => {
      return await axiosClient.post('/portal/admin/transfer-contact-driver-license-to-user', {
        contactId,
        clientUserId,
      })
    },

    approveTransporterDriverUserRequest: async (requestId: string): Promise<{ requestId: string; createdUserId: string }> => {
      return await axiosClient.post(`/portal/admin/transporter-driver-user-requests/${requestId}/approve`)
    },

    rejectTransporterDriverUserRequest: async (requestId: string, reason?: string): Promise<{ requestId: string }> => {
      return await axiosClient.post(`/portal/admin/transporter-driver-user-requests/${requestId}/reject`, {
        reason,
      })
    },

    deleteFirebaseUser: async (userId: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/delete-firebase-user', {
        userId,
      })
    },

    disableFirebaseUser: async (userId: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/disable-firebase-user', {
        userId,
      })
    },

    enableFirebaseUser: async (userId: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/enable-firebase-user', {
        userId,
      })
    },

    resendVerificationEmail: async (userId: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/resend-verification-email', {
        userId,
      })
    },

    sendPasswordReset: async (email: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/send-password-reset', {
        email,
      })
    },

    editFirebaseUser: async (userData: { userId: string; [key: string]: any }): Promise<any> => {
      return await axiosClient.post('/portal/admin/edit-firebase-user', userData)
    },

    previewMergePortalUsers: async (input: {
      survivorId: string
      duplicateId: string
    }): Promise<{
      survivorId: string
      duplicateId: string
      survivorEmail: string | null
      duplicateEmail: string | null
      survivorName: string
      duplicateName: string
      survivorRefs: Record<string, number>
      duplicateRefs: Record<string, number>
    }> => {
      return await axiosClient.post('/portal/admin/preview-merge-portal-users', input)
    },

    mergePortalUsers: async (input: {
      survivorId: string
      duplicateId: string
      preferredEmail?: string | null
      deleteDuplicate?: boolean
    }): Promise<any> => {
      return await axiosClient.post('/portal/admin/merge-portal-users', input)
    },

    generatePhotoUploadLink: async (email: string, vehicleData: any): Promise<any> => {
      return await axiosClient.post('/portal/admin/generate-photo-upload-link', {
        email,
        vehicleData,
      })
    },

    getAllClientFinancingProfiles: async (options?: {
      limit?: number
      page?: number
      lastDocId?: string | null
      search?: string
    }): Promise<any> => {
      const params = new URLSearchParams()
      if (options?.limit) params.append('limit', options.limit.toString())
      if (options?.page) params.append('page', options.page.toString())
      if (options?.lastDocId && options.lastDocId !== null) params.append('lastDocId', options.lastDocId)
      if (options?.search) params.append('search', options.search)

      const queryString = params.toString() ? `?${params.toString()}` : ''
      return await axiosClient.get(`/portal/admin/get-all-client-financing-profiles${queryString}`)
    },

    getClientFinancingProfilesById: async (userIds: string[]): Promise<ClientFinancingProfileResponse[]> => {
      const response = await axiosClient.post('/portal/admin/get-client-financing-profiles-by-id', { userIds })
      // Extract the data array from the response structure
      const profilesData = response.data || []
      // Ensure we always return an array
      if (!profilesData || !Array.isArray(profilesData)) {
        console.error('API returned invalid data format for client financing profiles by ID:', response)
        return []
      }
      return profilesData
    },

    getClientFinancingProfileVersion: async (clientId: string, versionId: string): Promise<any> => {
      const params = new URLSearchParams({
        clientId: encodeURIComponent(clientId),
        versionId: encodeURIComponent(versionId),
      })
      return await axiosClient.get(`/portal/admin/get-client-financing-profile-version?${params.toString()}`)
    },

    updateClientFinancingProfile: async (
      clientId: string,
      financingProfile: Partial<PrivateClientFinancingProfile>,
      versionName?: string,
    ): Promise<any> => {
      return await axiosClient.post('/portal/admin/update-client-financing-profile', {
        clientId,
        financingProfile,
        versionName,
      })
    },

    getClientBusinessProfiles: async (clientId: string): Promise<{ success: boolean; data: ClientBusinessProfileVariant[] }> => {
      const params = new URLSearchParams({
        clientId: encodeURIComponent(clientId),
      })
      return await axiosClient.get(`/portal/admin/get-client-business-profiles?${params.toString()}`)
    },

    createClientBusinessProfile: async (
      clientId: string,
      profileName: string,
      profileData: Partial<ClientBusinessProfileVariant>,
    ): Promise<{ success: boolean; data: { id: string; profile_name: string } }> => {
      return await axiosClient.post('/portal/admin/create-client-business-profile', {
        clientId,
        profileName,
        profileData,
      })
    },

    updateClientBusinessProfile: async (
      clientId: string,
      profileId: string,
      profileName: string,
      profileData: Partial<ClientBusinessProfileVariant>,
    ): Promise<{ success: boolean }> => {
      return await axiosClient.post('/portal/admin/update-client-business-profile', {
        clientId,
        profileId,
        profileName,
        profileData,
      })
    },

    rollbackToClientFinancingProfileVersion: async (clientId: string, versionId: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/rollback-to-client-financing-profile-version', {
        clientId,
        versionId,
      })
    },

    generateApplicationForUser: async (
      userId: string,
      bankId: string,
      useBankSpecificProfile?: boolean,
      selectedBusinessProfileId?: string,
    ): Promise<any> => {
      return await axiosClient.post('/panda/admin/generate-application-for-user', {
        userId,
        bankId,
        useBankSpecificProfile: useBankSpecificProfile || false,
        selectedBusinessProfileId,
      })
    },

    generateApplicationFromBankProfile: async (userId: string, bankId: string): Promise<any> => {
      return await axiosClient.post('/panda/admin/generate-application-for-user', {
        userId,
        bankId,
        useBankSpecificProfile: true,
      })
    },

    generateApplicationForUserFromVersion: async (
      userId: string,
      bankId: string,
      versionId: string,
    ): Promise<any> => {
      return await axiosClient.post('/panda/admin/generate-application-for-user-from-version', {
        userId,
        bankId,
        versionId,
      })
    },

    generateApplicationForCompany: async (companyId: string, userIds: string[], bankId: string): Promise<any> => {
      return await axiosClient.post('/panda/admin/generate-application-for-company', { companyId, userIds, bankId })
    },

    sendDraftApplication: async (applicationId: string): Promise<any> => {
      return await axiosClient.post('/panda/admin/send-draft-application', { applicationId })
    },

    resendApplicationEmail: async (applicationId: string): Promise<any> => {
      return await axiosClient.post('/panda/admin/resend-application-email', { applicationId })
    },

    resendPackageAssignedNotification: async (packageId: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/resend-package-assigned-notification', { packageId })
    },

    resendShipmentLegAssignedNotification: async (
      planId: string,
      legIndex: number
    ): Promise<any> => {
      return await axiosClient.post('/portal/admin/resend-shipment-leg-assigned-notification', {
        planId,
        legIndex,
      })
    },

    /** Ensures a non-expired token exists for the client (reuses existing when possible). */
    createDeliveryTrackingToken: async (body: {
      client_user_id: string
      expires_in_days?: number | null
    }): Promise<{
      token: string
      tracking_page_url: string
      public_api_path: string
      client_user_id: string
      expires_at: number | null
      reused_existing: boolean
    }> => {
      return await axiosClient.post('/portal/admin/create-delivery-tracking-token', body)
    },

    getGroupInsuranceCertificateUrl: async (
      groupId: string,
      storagePath: string
    ): Promise<{ url: string }> => {
      const params = new URLSearchParams({ groupId, storagePath })
      return await axiosClient.get(`/portal/admin/get-group-insurance-certificate-url?${params.toString()}`)
    },

    listLegPhotos: async (
      planId: string,
      legIndex: number,
      flow: 'preDelivery' | 'postDelivery' = 'preDelivery'
    ): Promise<{
      photos: Array<{
        id: string
        angle: string
        storagePath?: string
        url?: string
        uploadedAt?: unknown
        uploadedBy?: string
        mediaType?: 'photo' | 'video'
        isReplaced?: boolean
        replacedAt?: unknown
        originalUploadedAt?: unknown
      }>
    }> => {
      const res = await axiosClient.get(
        `/portal/admin/logistics/plans/${planId}/legs/${legIndex}/photos?flow=${flow}`
      ) as {
        data?: {
          photos: Array<{
            id: string
            angle: string
            storagePath?: string
            url?: string
            uploadedAt?: unknown
            uploadedBy?: string
            mediaType?: 'photo' | 'video'
            isReplaced?: boolean
            replacedAt?: unknown
            originalUploadedAt?: unknown
          }>
        }
      }
      return { photos: res?.data?.photos ?? [] }
    },

    uploadLegPhoto: async (
      planId: string,
      legIndex: number,
      angle: string,
      photoBase64: string,
      flow: 'preDelivery' | 'postDelivery' = 'preDelivery'
    ): Promise<{ storagePath: string; url?: string }> => {
      const res = await axiosClient.post(
        `/portal/admin/logistics/plans/${planId}/legs/${legIndex}/photos`,
        { angle, photo: photoBase64, flow }
      ) as { data?: { storagePath: string; url?: string } }
      return {
        storagePath: res?.data?.storagePath ?? '',
        url: res?.data?.url,
      }
    },

    uploadLegVideo: async (
      planId: string,
      legIndex: number,
      angle: string,
      file: File,
      flow: 'preDelivery' | 'postDelivery' = 'preDelivery'
    ): Promise<{ storagePath: string; url?: string }> => {
      const video = await fileToBase64(file)
      const res = await axiosClient.post(
        `/portal/admin/logistics/plans/${planId}/legs/${legIndex}/videos`,
        { angle, video, flow }
      ) as { data?: { storagePath: string; url?: string } }
      return {
        storagePath: res?.data?.storagePath ?? '',
        url: res?.data?.url,
      }
    },

    getPartsScrapeStatus: async (): Promise<{
      run: {
        id: string
        status: string
        trigger: string
        ok_count: number
        error_count: number
        skipped_count: number
        error_message: string | null
        created_at: string | null
        started_at: string | null
        finished_at: string | null
      } | null
    }> => {
      const res = (await axiosClient.get('/portal/admin/parts/scrape-status')) as {
        data?: {
          run: {
            id: string
            status: string
            trigger: string
            ok_count: number
            error_count: number
            skipped_count: number
            error_message: string | null
            created_at: string | null
            started_at: string | null
            finished_at: string | null
          } | null
        }
      }
      return { run: res?.data?.run ?? null }
    },

    queuePartsRescrape: async (): Promise<{ runId: string }> => {
      const res = (await axiosClient.post('/portal/admin/parts/rescrape')) as {
        data?: { runId: string }
      }
      return { runId: res?.data?.runId ?? '' }
    },

    listPartTrainingLabels: async (
      vehicleId?: string,
    ): Promise<{
      labels: Array<{
        id: string
        vehicle_id: string
        vehicle_label: string | null
        photo_url: string
        photo_path: string | null
        photo_source: string
        label: string
        labeled_by_name: string | null
        created_at: string | null
      }>
    }> => {
      const res = (await axiosClient.get('/portal/admin/parts/training-labels', {
        params: vehicleId ? { vehicleId } : undefined,
      })) as { data?: { labels: Array<{
        id: string
        vehicle_id: string
        vehicle_label: string | null
        photo_url: string
        photo_path: string | null
        photo_source: string
        label: string
        labeled_by_name: string | null
        created_at: string | null
      }> } }
      return { labels: res?.data?.labels ?? [] }
    },

    createPartTrainingLabel: async (payload: {
      vehicle_id: string
      vehicle_label?: string
      photo_url: string
      photo_path?: string
      photo_source?: 'inventory' | 'inspection' | 'damage'
      label: string
    }): Promise<{ id: string }> => {
      const res = (await axiosClient.post('/portal/admin/parts/training-labels', payload)) as {
        data?: { id: string }
      }
      return { id: res?.data?.id ?? '' }
    },

    getLegPhotosStatus: async (
      planId: string
    ): Promise<{ statusByLeg: Record<number, { hasPreDelivery: boolean; hasPostDelivery: boolean }> }> => {
      const res = await axiosClient.get(
        `/portal/admin/logistics/plans/${planId}/photos-status`
      ) as { data?: { statusByLeg: Record<number, { hasPreDelivery: boolean; hasPostDelivery: boolean }> } }
      return { statusByLeg: res?.data?.statusByLeg ?? {} }
    },

    getAllBanks: async (): Promise<{ banks: any[] }> => {
      return await axiosClient.get('/portal/admin/get-all-banks')
    },

    lookupUserByEmail: async (
      email: string,
    ): Promise<{ exists: boolean; userId?: string; email?: string }> => {
      try {
        const params = new URLSearchParams({ email })
        return (await axiosClient.get(
          `/portal/admin/check-user-exists?${params.toString()}`,
        )) as { exists: boolean; userId?: string; email?: string }
      } catch (error) {
        console.error('Error looking up user by email:', error)
        throw new Error('Failed to look up user by email')
      }
    },

    checkUserExists: async (email: string): Promise<boolean> => {
      try {
        const params = new URLSearchParams({ email })
        const result = (await axiosClient.get(
          `/portal/admin/check-user-exists?${params.toString()}`,
        )) as { exists: boolean }
        return result?.exists === true
      } catch (error) {
        console.error('Error checking if user exists:', error)
        throw new Error('Failed to check if user exists')
      }
    },

    extractInvoiceData: async (file: File): Promise<any> => {
      const fileData = await fileToBase64(file)
      return await axiosClient.post('/portal/admin/ai/extract-invoice-data', {
        fileData,
        fileType: file.type,
        fileName: file.name,
      })
    },

    cleanVehiclePhoto: async (file: File): Promise<any> => {
      const fileData = await fileToBase64(file)
      return await axiosClient.post('/portal/admin/ai/clean-vehicle-photo', {
        fileData,
      })
    },

    inspectVehicleDamage: async (file: File): Promise<any> => {
      const fileData = await fileToBase64(file)
      return await axiosClient.post('/portal/admin/ai/inspect-vehicle-damage', {
        fileData,
        fileType: file.type || 'image/webp',
        fileName: file.name || 'vehicle-photo.webp',
      })
    },

    // Bank-specific profile functions
    createBankSpecificProfile: async (clientId: string, bankId: string, versionName?: string): Promise<any> => {
      return await axiosClient.post('/portal/admin/create-bank-specific-profile', { clientId, bankId, versionName })
    },

    updateBankSpecificProfile: async (
      clientId: string,
      bankId: string,
      financingProfile: Partial<PrivateClientFinancingProfile>,
      versionName?: string,
    ): Promise<any> => {
      return await axiosClient.post('/portal/admin/update-bank-specific-profile', {
        clientId,
        bankId,
        financingProfile,
        versionName,
      })
    },

    getBankSpecificProfiles: async (clientId: string): Promise<any> => {
      const params = new URLSearchParams({
        clientId: encodeURIComponent(clientId),
      })
      return await axiosClient.get(`/portal/admin/get-bank-specific-profiles?${params.toString()}`)
    },

    getBankSpecificProfile: async (clientId: string, bankId: string): Promise<any> => {
      const params = new URLSearchParams({
        clientId: encodeURIComponent(clientId),
        bankId: encodeURIComponent(bankId),
      })
      return await axiosClient.get(`/portal/admin/get-bank-specific-profile?${params.toString()}`)
    },

    getBankSpecificProfileVersions: async (clientId: string, bankId: string): Promise<any> => {
      const params = new URLSearchParams({
        clientId: encodeURIComponent(clientId),
        bankId: encodeURIComponent(bankId),
      })
      return await axiosClient.get(`/portal/admin/get-bank-specific-profile-versions?${params.toString()}`)
    },

    getBankSpecificProfileVersion: async (clientId: string, bankId: string, versionId: string): Promise<any> => {
      const params = new URLSearchParams({
        clientId: encodeURIComponent(clientId),
        bankId: encodeURIComponent(bankId),
        versionId: encodeURIComponent(versionId),
      })
      return await axiosClient.get(`/portal/admin/get-bank-specific-profile-version?${params.toString()}`)
    },

    // Shippo shipping label functions
    createShippingLabel: async (labelData: any): Promise<any> => {
      return await axiosClient.post('/portal/admin/shippo/create-label', labelData)
    },

    getShippingLabel: async (params: {
      transaction_id?: string
      label_id?: string
      tracking_number?: string
      vehicle_id?: string
    }): Promise<any> => {
      const queryParams = new URLSearchParams()
      if (params.transaction_id) queryParams.append('transaction_id', params.transaction_id)
      if (params.label_id) queryParams.append('label_id', params.label_id)
      if (params.tracking_number) queryParams.append('tracking_number', params.tracking_number)
      if (params.vehicle_id) queryParams.append('vehicle_id', params.vehicle_id)

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      return await axiosClient.get(`/portal/admin/shippo/get-label${queryString}`)
    },

    getShippingRates: async (shipmentData: any): Promise<any> => {
      return await axiosClient.post('/portal/admin/shippo/get-rates', { shipment: shipmentData })
    },

    /** Get tracking status from Shippo (TRANSIT, DELIVERED, PRE_TRANSIT, etc.) for auto-advancing title status. Carrier is inferred from tracking number. */
    getTrackingStatus: async (trackingNumber: string): Promise<{ success: boolean; status: string | null }> => {
      const params = new URLSearchParams({ tracking_number: trackingNumber.trim() })
      // axiosClient response interceptor returns response.data, so res is the API body
      const res = await axiosClient.get(`/portal/admin/shippo/get-tracking-status?${params.toString()}`)
      return { success: (res as any)?.success ?? false, status: (res as any)?.status ?? null }
    },

    sendMarketingCampaign: async (input: {
      campaignId: string
      maxRecipients?: number
    }): Promise<{
      campaignId: string
      status: 'sending' | 'sent'
      eligibleCount: number
      queuedThisBatch: number
      sentThisBatch: number
      failedThisBatch: number
      sentTotal: number
      remaining: number
    }> => {
      return await axiosClient.post(
        '/portal/admin/send-marketing-campaign',
        {
          campaignId: input.campaignId,
          ...(input.maxRecipients ? { maxRecipients: input.maxRecipients } : {}),
        },
        { timeout: 180000 },
      )
    },
    sendMarketingCampaignTest: async (input: {
      campaignId: string
    }): Promise<{ to: string }> => {
      return await axiosClient.post(
        '/portal/admin/send-marketing-campaign-test',
        { campaignId: input.campaignId },
        { timeout: 60000 },
      )
    },
  },

  public: {
    generatePasswordReset: async (email: string, turnstileToken: string): Promise<void> => {
      await axiosPublicClient.post('/portal/public/generate-password-reset', {
        email,
        turnstileToken,
        'cf-turnstile-response': turnstileToken,
      })
    },

    resendVerificationEmail: async (email: string, turnstileToken: string): Promise<void> => {
      await axiosPublicClient.post('/portal/public/resend-verification-email', {
        email,
        turnstileToken,
        'cf-turnstile-response': turnstileToken,
      })
    },

    checkWebsiteApplyEmail: async (email: string): Promise<{ exists: boolean }> => {
      return (await axiosPublicClient.post('/portal/public/check-website-apply-email', {
        email,
      })) as { exists: boolean }
    },

    provisionWebsiteApplicant: async (email: string): Promise<void> => {
      await axiosPublicClient.post('/portal/public/provision-website-applicant', {
        email,
      })
    },

    submitWebsiteApplication: async (
      email: string,
      encryptedPayload: string,
    ): Promise<{ success: boolean; message?: string }> => {
      return (await axiosPublicClient.post('/portal/public/submit-website-application', {
        email,
        encryptedPayload,
      })) as { success: boolean; message?: string }
    },

    beforeSignIn: async (
      email: string | undefined,
      turnstileToken: string,
      action: 'login' | 'uploader-login' = 'login',
    ): Promise<void> => {
      await axiosPublicClient.post('/portal/public/before-sign-in-actions', {
        ...(email ? { email } : {}),
        action,
        turnstileToken,
        'cf-turnstile-response': turnstileToken,
      })
    },
  },

  shared: {
    setInitialPassword: async (password: string): Promise<any> => {
      return await axiosClient.post('/portal/shared/set-initial-password', {
        password,
      })
    },
  },

  transporter: {
    getPackages: async (): Promise<any> => {
      return await axiosClient.get('/portal/transporter/packages')
    },
  },
}
